<template>
    <div
        class="card bg-surface border-divider rounded-md border p-4 md:col-span-2"
    >
        <div class="mb-4 flex items-center justify-between">
            <div>
                <h4 class="text-body font-semibold">
                    {{ $t('sm_risk_disclaimers_title') }}
                </h4>
                <p class="text-muted text-xs">
                    {{ $t('sm_risk_disclaimers_help') }}
                </p>
            </div>
            <button
                type="button"
                class="btn btn-secondary btn-sm"
                @click="addRisk"
            >
                <i class="fas fa-plus mr-1"></i>
                {{ $t('sm_add_risk_disclaimer') }}
            </button>
        </div>

        <!-- Risk List Container (Native HTML5 Drag & Drop) -->
        <div
            v-if="risks.length > 0"
            class="space-y-3"
        >
            <div
                v-for="(element, index) in risks"
                :key="element.tempId"
                draggable="true"
                class="border-divider bg-background flex items-center gap-3 rounded-md border p-3 shadow-sm transition-opacity"
                :class="{ 'opacity-40': draggedIndex === index }"
                @dragstart="onDragStart(index)"
                @dragover.prevent
                @drop="onDrop(index)"
            >
                <!-- Drag Handle -->
                <div
                    class="text-muted cursor-grab px-1 hover:text-gray-700 active:cursor-grabbing"
                >
                    <i class="fas fa-grip-lines text-base"></i>
                </div>

                <!-- Index Badge -->
                <span
                    class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600"
                >
                    {{ index + 1 }}
                </span>

                <!-- Inputs Area -->
                <div class="grid flex-1 grid-cols-1 gap-3 md:grid-cols-12">
                    <!-- Text Input -->
                    <div class="md:col-span-8">
                        <input
                            v-model="element.text"
                            type="text"
                            class="form-input w-full text-sm"
                            :placeholder="$t('sm_risk_text_placeholder')"
                            required
                            @input="updateParent"
                        />
                    </div>

                    <!-- Optional URL Input -->
                    <div class="md:col-span-4">
                        <input
                            v-model="element.url"
                            type="url"
                            class="form-input w-full text-sm"
                            :placeholder="$t('sm_risk_url_placeholder')"
                            @input="updateParent"
                        />
                    </div>
                </div>

                <!-- Delete Button -->
                <button
                    type="button"
                    class="bg-danger-600 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-white disabled:cursor-not-allowed disabled:opacity-80"
                    :title="$t('remove')"
                    @click="removeRisk(index)"
                >
                    <fa icon="fas fa-trash-alt" />
                </button>
            </div>
        </div>

        <div
            v-else
            class="border-divider bg-background-muted rounded-md border border-dashed p-4 text-center"
        >
            <span class="text-muted text-sm">
                {{ $t('sm_no_risk_disclaimers') }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { SmAssetRisk } from '@src/types'

interface LocalRisk extends SmAssetRisk {
    tempId?: string
}

const props = defineProps<{
    modelValue?: LocalRisk[]
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: LocalRisk[]): void
}>()

const risks = ref<LocalRisk[]>([])
const draggedIndex = ref<number | null>(null)

/**
 * Synchronize component state with incoming v-model from parent.
 */
watch(
    () => props.modelValue,
    (newVal) => {
        if (newVal && Array.isArray(newVal)) {
            risks.value = newVal.map((item, index) => ({
                ...item,
                tempId: item.tempId || item.id || `temp-${index}-${Date.now()}`,
                sort_order: item.sort_order ?? index + 1,
            }))
        } else {
            risks.value = []
        }
    },
    { immediate: true, deep: true }
)

/**
 * Emit current risks array to parent form.
 */
function updateParent(): void {
    emit('update:modelValue', risks.value)
}

/**
 * Native Drag & Drop Handlers.
 */
function onDragStart(index: number): void {
    draggedIndex.value = index
}

function onDrop(dropIndex: number): void {
    if (draggedIndex.value === null || draggedIndex.value === dropIndex) return

    const itemToMove = risks.value.splice(draggedIndex.value, 1)[0]
    risks.value.splice(dropIndex, 0, itemToMove)

    draggedIndex.value = null
    updateSortOrders()
}

/**
 * Add a new risk disclaimer item.
 */
function addRisk(): void {
    risks.value.push({
        tempId: `new-${Date.now()}`,
        text: '',
        url: null,
        sort_order: risks.value.length + 1,
    })
    updateParent()
}

/**
 * Remove a risk disclaimer item by index.
 */
function removeRisk(index: number): void {
    risks.value.splice(index, 1)
    updateSortOrders()
}

/**
 * Recalculate sort_order indices and notify parent.
 */
function updateSortOrders(): void {
    risks.value.forEach((item, index) => {
        item.sort_order = index + 1
    })
    updateParent()
}
</script>
